/**
 * Keeping the screen on.
 *
 * A classroom tablet that sleeps is a tablet that shows nothing — the teacher
 * looks up at a black rectangle and has no idea whether the next bell is coming.
 * (The bells themselves keep ringing; audio does not need the screen. This is
 * about the wall display being a wall display.)
 *
 * Android drops the wake lock every time the page is backgrounded, and never
 * hands it back on its own, so the only workable pattern is to re-acquire on
 * `visibilitychange` — forever, for as long as the kiosk is running.
 *
 * The API is missing on plenty of Android WebViews and requires a secure
 * context. Where it is unavailable we return false and let the tablet's own
 * "stay awake while charging" setting do the work; nothing else in the app
 * depends on this succeeding.
 */

type Sentinel = { released: boolean; release(): Promise<void>; addEventListener: EventTarget['addEventListener'] };

let sentinel: Sentinel | null = null;
let watching = false;
let acquiring: Promise<boolean> | null = null;

function supported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'wakeLock' in navigator &&
    typeof (navigator as Navigator & { wakeLock?: { request?: unknown } }).wakeLock?.request ===
      'function'
  );
}

async function acquire(): Promise<boolean> {
  if (!supported()) return false;
  if (sentinel && !sentinel.released) return true;
  if (acquiring) return acquiring;

  acquiring = (async () => {
    try {
      const lock = (await navigator.wakeLock.request('screen')) as unknown as Sentinel;
      sentinel = lock;
      // Android fires this whenever it takes the lock back — on backgrounding,
      // on a system dialog, on low battery. Note it and wait for visibility.
      lock.addEventListener('release', () => {
        if (sentinel === lock) sentinel = null;
        console.info('[wake] screen wake lock released by the system');
      });
      console.info('[wake] screen wake lock held');
      return true;
    } catch (err) {
      // Denied (not visible, battery saver) or unsupported in this WebView.
      console.warn('[wake] could not hold the screen awake', err);
      return false;
    } finally {
      acquiring = null;
    }
  })();

  return acquiring;
}

/**
 * Ask for the lock, and keep asking for the rest of the session. Safe to call
 * more than once: the listeners are attached exactly once.
 */
export function requestWakeLock(): Promise<boolean> {
  if (!supported()) {
    console.info('[wake] no Screen Wake Lock API here; relying on the tablet display settings');
    return Promise.resolve(false);
  }

  if (!watching && typeof document !== 'undefined') {
    watching = true;
    // The lock cannot be requested while hidden, so the moment we are visible
    // again is the only moment worth trying. This is the whole trick.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void acquire();
    });
    // A kiosk that gets focus back after a system overlay is in the same boat.
    window.addEventListener('focus', () => void acquire());
  }

  return acquire();
}
